import { memo, useMemo } from 'react'

import { FloatingHoverCard } from '@/components/common/Floating'
import { type TypeaheadDynamicModeType, type TypeaheadOptionType, type TypeaheadStaticModeType } from '@/defs/input'
import { useTypeaheadDynamic } from '@/hooks/useTypeaheadDynamic'
import { useTypeaheadStatic } from '@/hooks/useTypeaheadStatic'
import { toArray } from '@/utils/commonUtil'
import { buildRec } from '@/utils/recUtil'

import { InputTypeaheadContent, type Props as PropsContent } from './InputTypeaheadContent/InputTypeaheadContent'

type PropsProvided<T extends string = string> = {
  mode?: never
} & PropsContent<T>

type PropsStatic = {
  mode: TypeaheadStaticModeType
} & PropsContent

type PropsDynamic = {
  mode: TypeaheadDynamicModeType
} & PropsContent

type Props<T extends string = string> = PropsProvided<T> | PropsStatic | PropsDynamic

const InputTypeaheadProvided = <T extends string = string>({ ...props }: PropsProvided<T>) => (
  <InputTypeaheadContent {...props} />
)

const InputTypeaheadStatic = ({ mode, ...props }: PropsStatic) => {
  const { loading, options } = useTypeaheadStatic({ mode })

  return <InputTypeaheadContent {...{ ...props, loading, options }} />
}

const InputTypeaheadDynamic = ({ mode, ...props }: PropsDynamic) => {
  const { loading, results, search, setSearch } = useTypeaheadDynamic(mode)
  const { value } = props

  const options = useMemo(() => {
    const names = search ? results.map(({ label }) => label) : value ? toArray(value) : []

    return names.map<TypeaheadOptionType<string>>((name) => ({
      Component: ['card', 'commander', 'partner'].includes(mode)
        ? ({ children }) => <FloatingHoverCard rec={buildRec({ name })}>{children}</FloatingHoverCard>
        : undefined,
      value: name,
    }))
  }, [results, search, value, mode])

  return <InputTypeaheadContent {...{ ...props, loading, onSearch: setSearch, options }} />
}

const InputTypeaheadInner = <T extends string = string>({ ...props }: Props<T>) =>
  !props.mode ? (
    <InputTypeaheadProvided {...props} />
  ) : props.mode === 'set' ? (
    <InputTypeaheadStatic {...props} />
  ) : (
    <InputTypeaheadDynamic {...props} />
  )

export const InputTypeahead = memo(InputTypeaheadInner) as typeof InputTypeaheadInner
